// @ts-nocheck
/**
 * Lookup TanStack Query hooks for the Cloud Telephony Users feature.
 * Each wraps a v1 `getApiCall` endpoint with sensible cache + enabled
 * gating so pages don't fire unnecessary requests.
 *
 * Modeled on obd3-gui's `features/users/lookups/api.ts` pattern.
 */
import { useQuery } from '@tanstack/react-query';
import { getApiCall } from '@/service/httpCall';
import {
  API_ACL_SETTING_LIST,
  API_COMPANY_LIST,
  API_CALL_CENTRE_LIST,
  API_DEMO_ACCOUNT_SETTINGS_LIST,
} from '@/service/url';
import type {
  AclSettingRow,
  CompanyRow,
  CallCentreRow,
  DemoAccountSettings,
} from './types';

/**
 * ACL settings for a specific target role. Empty target_role → hook is
 * disabled (no fire).
 */
export function useAclSettings(targetRole: string | null) {
  return useQuery({
    queryKey: ['users', 'acl-settings', targetRole ?? ''],
    queryFn: () =>
      getApiCall(`${API_ACL_SETTING_LIST}?target_role=${encodeURIComponent(targetRole || '')}&fields=min`, {}),
    enabled: Boolean(targetRole),
    staleTime: 5 * 60_000,
    select: (data: any) => (Array.isArray(data) ? (data as AclSettingRow[]) : []),
  });
}

/**
 * Company list. Some parents (SUPERADMIN) filter results client-side by
 * `created_by`; that logic stays in the page.
 */
export function useCompanyList(enabled = true) {
  return useQuery({
    queryKey: ['users', 'company-list'],
    queryFn: () => getApiCall(API_COMPANY_LIST, {}),
    enabled,
    staleTime: 60_000,
    select: (data: any) => (Array.isArray(data) ? (data as CompanyRow[]) : []),
  });
}

/**
 * Call-centre list. Skipped when creator is CALL_CENTRE_ADMIN (they own
 * a single call centre — resolved from the current user).
 */
export function useCallCentreList(enabled = true) {
  return useQuery({
    queryKey: ['users', 'call-centre-list'],
    queryFn: () => getApiCall(API_CALL_CENTRE_LIST, {}),
    enabled,
    staleTime: 60_000,
    select: (data: any) => (Array.isArray(data) ? (data as CallCentreRow[]) : []),
  });
}

/**
 * Demo-account settings (fires only when the creator's assigned company
 * is a demo company). Returned once per session — long stale time.
 */
export function useDemoAccountSettings(enabled = true) {
  return useQuery({
    queryKey: ['users', 'demo-account-settings'],
    queryFn: () => getApiCall(API_DEMO_ACCOUNT_SETTINGS_LIST, {}),
    enabled,
    staleTime: 10 * 60_000,
    select: (data: any) => (data as DemoAccountSettings) ?? {},
  });
}
